import { Popover } from "@headlessui/react";
import { range, sortBy } from "lodash";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import { usePopper } from "react-popper";
import { GetComplianceProgramForReportQuery } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import { TableBodyCell } from "@components/table/table";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import CalibrationCellPopover from "./calibration-cell-popover";
import { PerformanceAssessmentTableQuestion } from "./performance-assessment-table";
import usePrecalibrationData from "./use-precalibration-data";

const PerformanceAssessmentTablePrecalibrationCell = ({
  assessmentTemplate,
  target,
  question,
  canCalibrate,
  canSuggestCalibration,
}: {
  assessmentTemplate: NonNullable<
    NonNullable<
      GetComplianceProgramForReportQuery["complianceProgram"]
    >["performanceAssessmentTemplate"]
  >;
  target: BasicUser;
  question: PerformanceAssessmentTableQuestion;
  canCalibrate: boolean;
  canSuggestCalibration: boolean;
}) => {
  const { complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );
  const [popperRefElement, setPopperRefElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const { styles, attributes } = usePopper(popperRefElement, popperElement, {
    placement: "left-end",
  });

  const questionLabels = useMemo(
    () =>
      range(question.startValue, question.endValue + 1).reduce(
        (labelObj, value, index) => ({
          ...labelObj,
          [value]: question.labels[index] || value.toString(),
        }),
        {} as Record<string, string>,
      ),
    [question.endValue, question.labels, question.startValue],
  );

  const { precalibrationData } = usePrecalibrationData({
    complianceProgramId,
    templateId: assessmentTemplate.id,
  });
  const mostRecentCalibration = useMemo(() => {
    return sortBy(
      precalibrationData.filter(
        (calibration) =>
          !calibration.isSuggestion &&
          calibration.target?.id === target.id &&
          calibration.question.id === question.id,
      ),
      (calibration) => -moment(calibration.created).unix(),
    )[0];
  }, [precalibrationData, target, question]);

  return (
    <TableBodyCell
      className="p-0 h-full"
      key={question.id}
      aria-label="Performance assessment report precalibration cell"
    >
      <Popover className="h-full">
        {({ open }) => (
          <>
            <Popover.Button
              as="div"
              ref={setPopperRefElement}
              className="flex items-center hover:cursor-pointer hover:text-gray-700 hover:bg-black/5 px-5 py-3 w-full h-full"
              role="button"
            >
              <div className="flex items-center gap-2">
                <div
                  className={classNames(
                    "max-w-36 text-ellipsis overflow-hidden text-nowrap",
                    !mostRecentCalibration && "text-gray-500",
                  )}
                  title={
                    mostRecentCalibration
                      ? `${
                          questionLabels[
                            assertNonNull(mostRecentCalibration.integerAnswer)
                          ]
                        }`
                      : undefined
                  }
                >
                  {mostRecentCalibration
                    ? questionLabels[
                        assertNonNull(mostRecentCalibration.integerAnswer)
                      ]
                    : "-"}
                </div>
              </div>
            </Popover.Button>
            {open && (
              <CalibrationCellPopover
                assessmentTemplate={assessmentTemplate}
                target={target}
                canCalibrate={canCalibrate}
                canSuggestCalibration={canSuggestCalibration}
                questionLabels={questionLabels}
                attributes={attributes.popper ?? {}}
                question={question}
                styles={styles.popper}
                setElement={setPopperElement}
              />
            )}
          </>
        )}
      </Popover>
    </TableBodyCell>
  );
};

export default PerformanceAssessmentTablePrecalibrationCell;
